/**
 * WhatsApp Notification Service
 * Handles sending WhatsApp messages after job posting using webhook.whatapi.in
 */

// WhatsApp API Configuration
const WHATSAPP_CONFIG = {
  baseUrl: 'https://webhook.whatapi.in/webhook/691d6bfb1b9845c02d50aacb',
  defaultNumber: '917058869790', // Default recipient number
  timeout: 30000, // 30 seconds timeout
  retryAttempts: 3,
  retryDelay: 2000 // 2 seconds between retries
};

/**
 * Format job data into comma-separated message format
 * @param {Object} jobData - Job posting data
 * @param {Object} additionalData - Additional data like user info, package stats
 * @returns {string} Formatted message string
 */
const formatJobMessage = (jobData, additionalData = {}) => {
  try {
    console.log('📝 [WhatsApp] Formatting job message with data:', { jobData, additionalData });
    
    const {
      title = '',
      company = '',
      location = {},
      jobType = '',
      experienceLevel = '',
      workMode = '',
      description = ''
    } = jobData;

    const {
      employerName = 'Employer',
      remainingJobs = 0,
      totalJobs = 0,
      usedJobs = 0
    } = additionalData;

    // Format location string
    const locationStr = location.city && location.state 
      ? `${location.city}, ${location.state}` 
      : location.city || location.state || 'Not specified';

    // Create formatted message with proper escaping
    const messageData = {
      jobs: 1, // Always 1 for single job posting
      name: employerName,
      title: title || 'Job Position',
      company: company || 'Company',
      location: locationStr,
      jobtype: jobType || 'Full-time',
      experiencelevel: experienceLevel || 'Mid-level',
      workmode: workMode || 'Onsite',
      jobpostings: usedJobs + 1, // Current usage after this post
      remainingjob: Math.max(0, remainingJobs - 1), // Remaining after this post
      totalavailable: totalJobs || 0,
      text: description ? description.substring(0, 200).replace(/\n/g, ' ') : 'New job opportunity available',
      posted: new Date().toISOString().split('T')[0] // YYYY-MM-DD format
    };

    // Create comma-separated string
    const message = [
      messageData.jobs,
      messageData.name,
      messageData.title,
      messageData.company,
      messageData.location,
      messageData.jobtype,
      messageData.experiencelevel,
      messageData.workmode,
      messageData.jobpostings,
      messageData.remainingjob,
      messageData.totalavailable,
      messageData.text,
      messageData.posted
    ].join(',');

    console.log('✅ [WhatsApp] Message formatted successfully:', message.substring(0, 100) + '...');
    return message;
  } catch (error) {
    console.error('❌ [WhatsApp] Error formatting message:', error);
    // Return fallback message
    return `1,${additionalData.employerName || 'Employer'},Job Posted,${jobData.company || 'Company'},Location,Full-time,Mid-level,Onsite,1,0,1,New job posted successfully,${new Date().toISOString().split('T')[0]}`;
  }
};

/**
 * Send WhatsApp message with retry logic
 * @param {string} message - Formatted message string
 * @param {string} phoneNumber - Recipient phone number (optional)
 * @returns {Promise<Object>} Result object
 */
const sendWhatsAppMessage = async (message, phoneNumber = null) => {
  const targetNumber = phoneNumber || WHATSAPP_CONFIG.defaultNumber;
  let lastError = null;

  console.log(`📱 [WhatsApp] Attempting to send message to ${targetNumber}`);
  console.log(`📄 [WhatsApp] Message preview: ${message.substring(0, 150)}...`);

  for (let attempt = 1; attempt <= WHATSAPP_CONFIG.retryAttempts; attempt++) {
    try {
      console.log(`🔄 [WhatsApp] Attempt ${attempt}/${WHATSAPP_CONFIG.retryAttempts}`);

      // Encode message for URL
      const encodedMessage = encodeURIComponent(message);
      
      // Build the webhook URL
      const webhookUrl = `${WHATSAPP_CONFIG.baseUrl}?number=${targetNumber}&message=${encodedMessage}`;
      
      console.log(`🌐 [WhatsApp] Webhook URL: ${webhookUrl.substring(0, 100)}...`);

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), WHATSAPP_CONFIG.timeout);

      // Make the API call
      const response = await fetch(webhookUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'HarshalJobs-WhatsApp-Service/1.0'
        }
      });

      clearTimeout(timeoutId);

      console.log(`📊 [WhatsApp] Response status: ${response.status}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse response
      const result = await response.json();
      console.log('📥 [WhatsApp] API Response:', result);

      if (result.accepted === true || result.success === true) {
        console.log('✅ [WhatsApp] Message sent successfully!');
        return {
          success: true,
          message: 'WhatsApp message sent successfully',
          data: result,
          attempt,
          timestamp: new Date().toISOString()
        };
      } else {
        throw new Error(`API rejected message: ${JSON.stringify(result)}`);
      }

    } catch (error) {
      lastError = error;
      console.error(`❌ [WhatsApp] Attempt ${attempt} failed:`, error.message);

      if (error.name === 'AbortError') {
        console.error(`⏰ [WhatsApp] Request timed out after ${WHATSAPP_CONFIG.timeout}ms`);
      }

      // If this isn't the last attempt, wait before retrying
      if (attempt < WHATSAPP_CONFIG.retryAttempts) {
        console.log(`⏳ [WhatsApp] Waiting ${WHATSAPP_CONFIG.retryDelay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, WHATSAPP_CONFIG.retryDelay));
      }
    }
  }

  // All attempts failed
  console.error(`💥 [WhatsApp] All ${WHATSAPP_CONFIG.retryAttempts} attempts failed. Last error:`, lastError?.message);
  return {
    success: false,
    error: lastError?.message || 'Unknown error occurred',
    attempts: WHATSAPP_CONFIG.retryAttempts,
    timestamp: new Date().toISOString()
  };
};

/**
 * Send job posting notification via WhatsApp
 * @param {Object} jobData - Job posting data
 * @param {Object} userInfo - User information
 * @param {Object} packageInfo - Package/subscription information
 * @returns {Promise<Object>} Result object
 */
export const sendJobPostingNotification = async (jobData, userInfo = {}, packageInfo = {}) => {
  try {
    console.log('🚀 [WhatsApp] Starting job posting notification process');
    console.log('📋 [WhatsApp] Job data:', { title: jobData.title, company: jobData.company });
    console.log('👤 [WhatsApp] User info:', { name: userInfo.name, phone: userInfo.phone });
    console.log('📦 [WhatsApp] Package info:', packageInfo);

    // Validate required data
    if (!jobData || !jobData.title) {
      throw new Error('Job data is required with at least a title');
    }

    // Prepare additional data for message formatting
    const additionalData = {
      employerName: userInfo.name || userInfo.firstName || 'Employer',
      remainingJobs: packageInfo.remainingJobs || 0,
      totalJobs: packageInfo.totalJobs || 0,
      usedJobs: packageInfo.usedJobs || 0
    };

    // Format the message
    const formattedMessage = formatJobMessage(jobData, additionalData);

    // Determine phone number (user's phone or default)
    const phoneNumber = userInfo.phone || WHATSAPP_CONFIG.defaultNumber;

    // Send the message
    const result = await sendWhatsAppMessage(formattedMessage, phoneNumber);

    if (result.success) {
      console.log('🎉 [WhatsApp] Job posting notification sent successfully!');
    } else {
      console.error('💔 [WhatsApp] Failed to send job posting notification');
    }

    return result;

  } catch (error) {
    console.error('💥 [WhatsApp] Error in sendJobPostingNotification:', error);
    return {
      success: false,
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Test the WhatsApp service with dummy data
 * @returns {Promise<Object>} Test result
 */
export const testWhatsAppService = async () => {
  console.log('🧪 [WhatsApp] Running service test...');
  
  const dummyJobData = {
    title: 'Software Engineer',
    company: 'Tech Corp',
    location: { city: 'Mumbai', state: 'Maharashtra' },
    jobType: 'Full-time',
    experienceLevel: 'Mid-level',
    workMode: 'Remote',
    description: 'Exciting opportunity for a software engineer with experience in JavaScript and Node.js.'
  };

  const dummyUserInfo = {
    name: 'John Doe',
    phone: '917058869790'
  };

  const dummyPackageInfo = {
    remainingJobs: 5,
    totalJobs: 10,
    usedJobs: 4
  };

  return await sendJobPostingNotification(dummyJobData, dummyUserInfo, dummyPackageInfo);
};

/**
 * Get WhatsApp service configuration
 * @returns {Object} Configuration object
 */
export const getWhatsAppConfig = () => {
  return { ...WHATSAPP_CONFIG };
};

/**
 * Update WhatsApp service configuration
 * @param {Object} newConfig - New configuration values
 */
export const updateWhatsAppConfig = (newConfig) => {
  Object.assign(WHATSAPP_CONFIG, newConfig);
  console.log('⚙️ [WhatsApp] Configuration updated:', WHATSAPP_CONFIG);
};

export default {
  sendJobPostingNotification,
  testWhatsAppService,
  getWhatsAppConfig,
  updateWhatsAppConfig
};
